import React, {useState, useEffect, useContext} from 'react';
import axios from 'axios';
import {ApiContext} from "../../contexts/ApiContext";
import {useAuth} from "../../../providers/authProvider";
import DefaultLayout from "../../../layouts/DefaultLayout";
import BoxLayout from "../../../layouts/BoxLayout";
import {Table} from "../../tables/BasicTable";
import H2Header from "../../headers/h2";
import {FaCheck, FaEye} from "react-icons/fa";
import DeclineButton from "../../buttons/DeclineButton";
import {useNavigate} from "react-router-dom";
import {acceptAndCreateRelation, declineHomeVisitRequest} from "../../../utils/homeVisitRequest";
import AcceptRequestModal from "../../modals/AcceptRequestModal";

function AdminHomeVisitsRequestsPage() {
    const {Url} = useContext(ApiContext)
    const {token} = useAuth();
    const [requests, setRequests] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<any>(null);
    const navigate = useNavigate();

    const handleShow = (request: any) => {
        // Navigate to a details page or open a modal
        navigate(`/admin/home-visit-requests/${request.ID}`, { replace: false });
    };

    const handleAcceptClick = (request: any) => {
        setSelectedRequest(request);
        setIsModalOpen(true);
    };

    const handleAccept = async (timeData: any) => {
        if (!selectedRequest) return;
        
        setLoading(true);
        setError(null);
        
        try {
            await acceptAndCreateRelation(selectedRequest.ID, timeData, selectedRequest, Url, token);
            // Refresh the requests list
            await fetchRequests();
            setIsModalOpen(false);
            setSelectedRequest(null);
        } catch (err) {
            console.error('Error accepting request:', err);
            setError('Het accepteren van de aanvraag of het aanmaken van de relatie is mislukt');
        } finally {
            setLoading(false);
        }
    };

    const handleDecline = async (request: any) => {
        setLoading(true);
        setError(null);
        
        try {
            await declineHomeVisitRequest(request.ID, Url, token);
            // Refresh the requests list
            await fetchRequests();
        } catch (err) {
            console.error('Error declining request:', err);
            setError('Het afwijzen van de aanvraag is mislukt');
        } finally {
            setLoading(false);
        }
    };

    const getColumns = () => [
        {
            Header: "Naam",
            accessor: (d: any) => `${d.FirstName} ${d.LastName}`,
        },
        {
            Header: "Soort",
            accessor: "HomeVisitCategoryName",
        },
        {
            Header: "Datum",
            accessor: "Date",
        },
        {
            Header: "Starttijd",
            accessor: "StartTime",
        },
        {
            Header: "Adres",
            accessor: "Address",
        },
        {
            Header: "Plaats",
            accessor: "City",
        },
        {
            Header: "Acties",
            id: "actions",
            Cell: ({row}: any) => (
                <div className="flex gap-2 items-center">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleShow(row.original);
                        }}
                        className="p-2 hover:bg-blue-100 rounded-full transition-colors"
                        title="Bekijken"
                    >
                        <FaEye className="text-blue-600" size={18} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptClick(row.original);
                        }}
                        className="p-2 hover:bg-green-100 rounded-full transition-colors"
                        title="Accepteren"
                    >
                        <FaCheck className="text-green-600" size={18} />
                    </button>
                    <DeclineButton
                        confirmText={`Weet je zeker dat je de aanvraag van ${row.original.FirstName} ${row.original.LastName} wilt afwijzen?`}
                        onConfirm={() => handleDecline(row.original)}
                    />
                </div>
            ),
        },
    ];

    const fetchRequests = async () => {
        try {
            const response = await axios.get(Url + 'admin/home-visit-requests', {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });

            setRequests(response.data);
            setError(null);
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                setError('Er zijn geen huisbezoek aanvragen gevonden');
            } else {
                setError('Het ophalen van huisbezoek aanvragen is mislukt');
            }
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Url, token]);

    let dataTable;
    if (requests.length > 0) {
        dataTable = <Table tableData={requests} getColumns={getColumns()} storageKey={"HomeVisitRequests"} />;
    } else {
        dataTable = <div>Er zijn nog geen huisbezoek aanvragen gevonden.</div>;
    }

    return (
        <DefaultLayout>
            <AcceptRequestModal 
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedRequest(null);
                }}
                onAccept={handleAccept}
            />
            
            {loading ? (
                <BoxLayout><div>Loading...</div></BoxLayout>
            ) : error ? (
                <BoxLayout><div className="text-red-500">{error}</div></BoxLayout>
            ) : (
                <BoxLayout>
                    <H2Header headerText="Huisbezoek Aanvragen" />
                    {dataTable}
                </BoxLayout>
            )}
        </DefaultLayout>
    );
}

export default AdminHomeVisitsRequestsPage;